/**
 * Shared upload primitives — used by the New Screening wizard and the
 * per-member document dialog on the Household Profile (v1.0.7 Batch B).
 */
import { useId, useState } from "react";
import { apiClient, formatApiError } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Upload, FileText, Paperclip, X } from "lucide-react";
import { toast } from "sonner";

export const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;

export function fmtSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Upload helper — sequential, returns {ok, file, data?, error?}. */
export async function uploadFileToScreening(screeningId, file, label) {
  const fd = new FormData();
  fd.append("file", file);
  try {
    const { data } = await apiClient.post(
      `/files/upload?screening_id=${screeningId}&label=${encodeURIComponent(label)}`,
      fd, { headers: { "Content-Type": "multipart/form-data" } },
    );
    return { ok: true, file, data };
  } catch (e) {
    return { ok: false, file, error: formatApiError(e.response?.data?.detail) || e.message };
  }
}

/** Single-file field (e.g. the rental application PDF). */
export function SingleFileField({ file, onChange, testidPrefix, title, hint, required, compact = false }) {
  const inputId = useId();
  const [dragOver, setDragOver] = useState(false);

  const ingest = (list) => {
    const f = list[0];
    if (!f) return;
    if (f.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`${f.name} is too large (max 25 MB)`);
      return;
    }
    onChange(f);
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    ingest(Array.from(e.dataTransfer?.files || []));
  };

  return (
    <div data-testid={`${testidPrefix}-section`}>
      <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568] flex items-center gap-2">
        <Paperclip className="h-3 w-3" /> {title}{required && <span className="text-[#EF4444] ml-1">*</span>}
      </Label>
      {hint && <p className="text-[11px] text-[#4A5568] mt-1.5 mb-3">{hint}</p>}

      <input id={inputId} type="file" accept="application/pdf,.pdf,image/*"
             onChange={(e) => { ingest(Array.from(e.target.files || [])); e.target.value = ""; }}
             className="hidden" data-testid={`${testidPrefix}-input`} />

      {!file ? (
        <label htmlFor={inputId}
               onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
               onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }}
               onDrop={onDrop}
               className={`flex items-center justify-center gap-2 border-2 border-dashed rounded-sm cursor-pointer transition-all text-center ${compact ? "py-3 px-3" : "py-6 px-4"} ${
                 dragOver
                   ? "border-[#0A192F] bg-[#0A192F]/8 text-[#0A192F] scale-[1.01]"
                   : "border-[#0A192F]/25 hover:border-[#0A192F]/45 hover:bg-[#F3EFEA]/40 text-[#4A5568] hover:text-[#0A192F]"
               }`}
               data-testid={`${testidPrefix}-trigger`}>
          <Upload className={compact ? "h-4 w-4" : "h-5 w-5"} />
          <span className={`font-medium ${compact ? "text-sm" : ""}`}>
            {dragOver ? "Drop file" : "Drag & drop, or click to choose"}
          </span>
        </label>
      ) : (
        <div className="flex items-center gap-3 px-3 py-2 bg-[#F3EFEA]/40 border border-[#0A192F]/10 rounded-sm"
             data-testid={`${testidPrefix}-selected`}>
          <FileText className="h-5 w-5 text-[#0A192F] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm text-[#0A192F] truncate">{file.name}</div>
            <div className="text-[10px] font-mono tracking-wider text-[#4A5568] uppercase">{fmtSize(file.size)}</div>
          </div>
          <button type="button" onClick={() => onChange(null)}
                  className="h-7 w-7 rounded-sm flex items-center justify-center text-[#EF4444] hover:bg-[#EF4444]/10"
                  data-testid={`${testidPrefix}-remove`} title="Remove">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

/** Multi-file field for supporting documents. */
export function MultiFileField({ files, onChange, testidPrefix, title, hint, compact = false }) {
  const inputId = useId();
  const [dragOver, setDragOver] = useState(false);

  const ingest = (list) => {
    const valid = [];
    const rejected = [];
    for (const f of list) {
      if (f.size > MAX_FILE_SIZE_BYTES) rejected.push(`${f.name} (${fmtSize(f.size)})`);
      else valid.push(f);
    }
    if (rejected.length > 0) toast.error(`Too large (max 25 MB): ${rejected.join(", ")}`);
    if (valid.length > 0) onChange([...files, ...valid]);
  };

  const remove = (idx) => onChange(files.filter((_, i) => i !== idx));

  return (
    <div data-testid={`${testidPrefix}-section`}>
      <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568] flex items-center gap-2">
        <Paperclip className="h-3 w-3" /> {title}
      </Label>
      {hint && <p className="text-[11px] text-[#4A5568] mt-1.5 mb-3">{hint}</p>}

      <input id={inputId} type="file" multiple
             onChange={(e) => { ingest(Array.from(e.target.files || [])); e.target.value = ""; }}
             className="hidden" data-testid={`${testidPrefix}-input`} />
      <label htmlFor={inputId}
             onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
             onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }}
             onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); ingest(Array.from(e.dataTransfer?.files || [])); }}
             className={`flex items-center justify-center gap-2 border-2 border-dashed rounded-sm cursor-pointer transition-all text-center ${compact ? "py-3 px-3" : "py-4 px-4"} ${
               dragOver
                 ? "border-[#0A192F] bg-[#0A192F]/8 text-[#0A192F] scale-[1.01]"
                 : "border-[#0A192F]/20 hover:border-[#0A192F]/40 hover:bg-[#F3EFEA]/40 text-[#4A5568] hover:text-[#0A192F]"
             }`}
             data-testid={`${testidPrefix}-trigger`}>
      <Upload className="h-4 w-4" />
        <span className={`font-medium ${compact ? "text-sm" : ""}`}>{dragOver ? "Drop files" : "Drag & drop, or click to add files"}</span>
        {!dragOver && !compact && <span className="text-[11px] text-[#4A5568]">· up to 25 MB each</span>}
      </label>

      {files.length > 0 && (
        <ul className="mt-3 space-y-2" data-testid={`${testidPrefix}-list`}>
          {files.map((f, idx) => (
            <li key={`${f.name}-${idx}`}
                className="flex items-center gap-3 px-3 py-2 bg-[#F3EFEA]/40 border border-[#0A192F]/10 rounded-sm"
                data-testid={`${testidPrefix}-item-${idx}`}>
              <FileText className="h-4 w-4 text-[#0A192F] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[#0A192F] truncate">{f.name}</div>
                <div className="text-[10px] font-mono tracking-wider text-[#4A5568] uppercase">{fmtSize(f.size)}</div>
              </div>
              <button type="button" onClick={() => remove(idx)}
                      className="h-7 w-7 rounded-sm flex items-center justify-center text-[#EF4444] hover:bg-[#EF4444]/10"
                      data-testid={`${testidPrefix}-remove-${idx}`} title="Remove">
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
